import { useState } from "react";
import BetSlip, { type Refusal } from "../components/BetSlip";
import { copy } from "../copy";
import { primaryButton } from "../contest/ui";
import { postJSON, reasonOf, withPick, type Market, type MarketOutcome } from "../contest/api";
import { pickKey, type LandingLiveContest, type Pick } from "../feed";
import { insufficientCredits } from "../picking";
import { centsPerDollar } from "../tiers";
import { cosignCopy } from "./cosignCopy";
import type { ProducerContest } from "./api";

type Props = {
  contest: ProducerContest;
  username: string;
  market: Market;
  outcome: MarketOutcome;
  onMarket: (market: Market) => void;
};

// The slip speaks the landing feed's shape, so the market read is mapped
// onto it once here, as the contest page does. Every figure is the
// server's: the browser derives no money.
function asLine(contest: ProducerContest, market: Market): LandingLiveContest {
  return {
    slug: contest.slug,
    name: contest.name,
    cover_url: contest.cover_url,
    ends_at: contest.ends_at ?? "",
    pool_cents: market.pool_cents,
    take_bps: market.take_bps,
    rake_bps: market.rake_bps,
    producer_bps: market.producer_bps,
    backing_bps: market.backing_bps,
    is_seeded: false,
    outcomes: market.outcomes.map((o) => ({ ...o })),
  };
}

// A co-sign is a pick on this producer placed from their own page: the same
// stake, the same pool, the same service. What makes it a co-sign is that
// the fan came for the producer rather than the contest, so the panel leads
// with what the producer keeps.
export default function CoSignPanel({ contest, username, market, outcome, onMarket }: Props) {
  const v = copy.contest.vote;
  const [selection, setSelection] = useState<Pick | null>(null);
  const line = asLine(contest, market);
  const mine = market.viewer.picks.find((p) => p.producer_id === outcome.producer_id);

  const place = async (amountCents: number, idempotencyKey: string): Promise<string | Refusal | null> => {
    const reply = await postJSON("/v1/web/market/pick", {
      market_id: market.market_id,
      producer_id: outcome.producer_id,
      amount_cents: amountCents,
      idempotency_key: idempotencyKey,
    });
    if (reply.status === 402) {
      return insufficientCredits(amountCents, market.viewer.balance_cents ?? 0, {
        reason: v.insufficient,
        noCheckout: v.insufficientNoCheckout,
      });
    }
    if (!reply.ok) return reasonOf(reply) ?? cosignCopy.failed;
    onMarket(withPick(market, reply.body));
    setSelection(null);
    return null;
  };

  return (
    <>
      <button
        type="button"
        data-testid="producer-cosign"
        onClick={() => setSelection({ contest: line, outcome: { ...outcome } })}
        className={`${primaryButton} w-full`}
      >
        {cosignCopy.action(username)}
      </button>
      {market.backing_bps > 0 && (
        <p data-testid="producer-cosign-keeps" className="mt-2 text-[11px] leading-relaxed text-money">
          {cosignCopy.keeps(username, centsPerDollar(market.backing_bps))}
        </p>
      )}
      {mine && (
        <p data-testid="producer-cosign-mine" className="mt-2 text-[11px] font-bold text-chrome" aria-live="polite">
          {cosignCopy.mine(mine.amount_display)}
        </p>
      )}
      {selection && (
        <BetSlip key={pickKey(selection)} pick={selection} onClose={() => setSelection(null)} onPlace={place} />
      )}
    </>
  );
}
